const fire = (el) => {
  el.dispatchEvent(new Event("input", { bubbles: true }))
  el.dispatchEvent(new Event("change", { bubbles: true }))
}

class VariantFabricPicker extends HTMLElement {
  async connectedCallback() {
    const productHandle = this.dataset.fabricColorProductHandle
    if (!productHandle) return

    this.boundOutsideClickHandler = this.handleOutsideClick.bind(this)
    this.boundEscapeHandler = this.handleEscape.bind(this)
    this.boundReposition = this.positionPanel.bind(this)

    const product = await this.fetchProductData(productHandle)
    if (!product || !product.variants) return

    this.variants = product.variants

    this.cascadeRoot = this.querySelector("[data-fabric-cascade]")
    this.cascadeTrigger = this.querySelector("[data-cascade-trigger]")
    this.cascadeTriggerText = this.querySelector("[data-cascade-trigger-text]")
    this.cascadePanel = this.querySelector("[data-cascade-panel]")
    this.cascadeGroups = this.querySelector("[data-cascade-groups]")
    this.cascadeColors = this.querySelector("[data-cascade-colors]")
    this.cascadeBackButton = this.querySelector("[data-cascade-back]")
    this.cascadeBackLabel = this.querySelector("[data-cascade-back-label]")
    this.colorFieldset = this.cascadeColors // legacy alias for helpers
    this.mobileMQ = window.matchMedia("(max-width: 749px)")

    this.variantInput = this.querySelector("[data-accessory-variant-id]")
    this.materialDisplay = this.getMaterialDisplayTarget()
    this.materialDisplayThumb = this.materialDisplay?.querySelector(".material-selection-display__thumb")
    this.materialDisplayText = this.materialDisplay?.querySelector(".material-selection-display__text")

    this.triggerPlaceholder = this.cascadeTriggerText?.textContent?.trim() || ""

    // Determine initial active group: from selected variant, fall back to first group
    const initialGroup = this.getInitialActiveGroup()
    if (initialGroup) this.setActiveGroup(initialGroup)

    this.broadcastValidity()
    this.hideMaterialDisplay()
    this.updateTriggerText()
    this.closePanel()
    this.bindEvents()

    // Initial price event for already set value
    if (this.variantInput.value) {
      const checkedRadio = this.cascadeColors.querySelector('input[type="radio"]:checked')
      if (checkedRadio) {
        setTimeout(() => {
          this.dispatchEvent(
            new CustomEvent("fabric:pricechange", {
              bubbles: true,
              detail: {
                productId: this.dataset.fabricColorProductHandle,
                variantId: this.variantInput.value,
                price: this.#parsePriceValue(checkedRadio.dataset.accessoryVariantPrice),
                compareAtPrice: this.#parsePriceValue(checkedRadio.dataset.accessoryVariantCompareAtPrice),
                name: checkedRadio.dataset.accessoryVariantName,
                key: checkedRadio.dataset.key,
              },
            }),
          )
        }, 0)
      }
    }
  }

  async fetchProductData(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`)
      return await res.json()
    } catch (e) {
      console.error("Error loading product data:", e)
      return null
    }
  }

  getInitialActiveGroup() {
    // Prefer the group of an already-selected variant, otherwise the first group rendered.
    if (this.variantInput?.value) {
      const checkedRadio = this.cascadeColors?.querySelector('input[type="radio"]:checked')
      if (checkedRadio) {
        const material = this.getMaterialFromVariantName(checkedRadio.getAttribute("data-accessory-variant-name"))
        if (material) return material
      }
    }
    const first = this.cascadeGroups?.querySelector("[data-cascade-group]")
    return first?.getAttribute("data-cascade-group") || ""
  }

  setActiveGroup(material) {
    if (!material) return
    this.activeGroup = material

    this.cascadeGroups?.querySelectorAll("[data-cascade-group]").forEach((btn) => {
      const isActive = btn.getAttribute("data-cascade-group") === material
      btn.classList.toggle("is-active", isActive)
      btn.setAttribute("aria-current", isActive ? "true" : "false")
    })

    const isQuickship = material === "__quickship"
    this.cascadeColors?.querySelectorAll("[data-cascade-color]").forEach((li) => {
      const isMatch = isQuickship
        ? li.hasAttribute("data-quickship")
        : li.getAttribute("data-material") === material
      li.hidden = !isMatch
      if (!isMatch) return

      const titleEl = li.querySelector(".variant-title")
      if (!titleEl) return
      const original = titleEl.getAttribute("data-label-original") || titleEl.textContent || ""
      titleEl.textContent = isQuickship
        ? original.replace(/\s*,\s*/, " – ")
        : (original.split(",").pop() || "").trim()
    })

    if (this.cascadeBackLabel) {
      this.cascadeBackLabel.textContent = isQuickship ? "Quickship" : material
    }
  }

  setDetailView(showDetail) {
    if (!this.cascadePanel) return
    this.cascadePanel.setAttribute("data-cascade-view", showDetail ? "detail" : "overview")
  }

  isMobile() {
    return this.mobileMQ?.matches ?? false
  }

  pickerHasValue(picker) {
    if (picker.hasAttribute("data-optional")) return true

    // Read directly from the hidden form input — it stays inside the picker
    // even while the dropdown panel is portaled to <body>.
    const variantInput = picker.querySelector("[data-accessory-variant-id]")
    if (!variantInput) return false

    const value = (variantInput.value || "").trim()
    const zeroish = /^(?:0|€?\s*0(?:[.,]00)?)$/
    return !!value && value !== "null" && value !== "undefined" && !zeroish.test(value)
  }

  broadcastValidity() {
    const pickers = document.querySelectorAll("variant-fabric-picker")
    const allValid = pickers.length === 0 || [...pickers].every(this.pickerHasValue)
    window.dispatchEvent(new CustomEvent("fabric:validity", { detail: { allValid } }))
  }

  /**
   * Universal price info for fabrics & upgrades
   */
  updateAddonPriceInfo(type, name, price, key) {
    if (!key) return
    const esc = (v) => (window.CSS && CSS.escape ? CSS.escape(v) : v)
    const selector = `[data-price-info][data-key="${esc(key)}"]`

    const box = document.querySelector(selector)
    if (!box) return

    const priceEl = box.querySelector("[data-info-price]")
    const nameEl = box.querySelector(type === "upgrade" ? "[data-info-upgrade]" : "[data-info-fabric]") || box.querySelector("[data-info-name]")
    if (!priceEl && !nameEl) return

    const isNullish = price == null || price === "" || price === "undefined" || price === "null"

    if (isNullish) {
      box.hidden = true
      return
    }

    if (nameEl) nameEl.textContent = name ?? ""
    if (priceEl) priceEl.textContent = "+ " + price
    box.hidden = false
  }

  updatePriceInfo(fabricName, fabricPrice, fabricKey) {
    this.updateAddonPriceInfo("fabric", fabricName, fabricPrice, fabricKey)
  }

  bindEvents() {
    this.cascadeTrigger?.addEventListener("click", (e) => {
      e.stopPropagation()
      if (this.isPanelOpen()) {
        this.closePanel()
      } else {
        this.openPanel()
      }
    })

    this.cascadeGroups?.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-cascade-group]")
      if (!btn) return
      e.preventDefault()
      const material = btn.getAttribute("data-cascade-group")
      this.setActiveGroup(material)
      if (this.isMobile()) this.setDetailView(true)
    })

    this.cascadeBackButton?.addEventListener("click", (e) => {
      e.preventDefault()
      this.setDetailView(false)
    })

    this.cascadeColors?.addEventListener("change", (e) => {
      if (e.target && e.target.matches('input[type="radio"]')) {
        this.updateVariantInputFromRadio(e.target)
        const selectedMaterial = this.getMaterialFromVariantName(e.target.getAttribute("data-accessory-variant-name"))
        if (selectedMaterial) this.setActiveGroup(selectedMaterial)
        this.updateTriggerText()
        this.setDetailView(false)
        this.closePanel()
      }
    })

    this.variantInput.addEventListener("change", () => {
      this.broadcastValidity()
      if (this.variantInput.hasAttribute("data-accessory-variant-price-update")) {
        this.updatePriceInfo(
          this.variantInput.getAttribute("data-accessory-variant-name"),
          this.variantInput.getAttribute("data-accessory-variant-price"),
          this.variantInput.getAttribute("data-key"),
        )
      }
    })

    // Bind upgrades (selects) — unchanged from previous version
    const upgradeSelects = Array.from(document.querySelectorAll("select[data-upgrade-product]"))
    upgradeSelects.forEach((sel) => {
      sel.addEventListener("change", () => {
        const opt = sel.options[sel.selectedIndex]
        const selectedValue = sel.value
        const upgradeKey = sel.dataset.key || sel.id || sel.name || ""

        if (!selectedValue) {
          this.updateAddonPriceInfo("upgrade", null, "null", upgradeKey)
          return
        }

        const upgradeName = opt?.getAttribute("data-upgrade-variant-name") ?? ""
        const upgradePrice = opt?.getAttribute("data-upgrade-variant-price") ?? "null"
        this.updateAddonPriceInfo("upgrade", upgradeName, upgradePrice, upgradeKey)
      })
    })
  }

  updateVariantInputFromRadio(input, { silent = false } = {}) {
    if (!input) return

    this.variantInput.value = input.value
    this.variantInput?.setAttribute("data-accessory-variant-price-update", input.getAttribute("data-accessory-variant-price-update"))
    this.variantInput?.setAttribute("data-accessory-variant-price", input.getAttribute("data-accessory-variant-price"))
    this.variantInput?.setAttribute("data-accessory-variant-compare-at-price", input.getAttribute("data-accessory-variant-compare-at-price"))
    this.variantInput?.setAttribute("data-accessory-variant-name", input.getAttribute("data-accessory-variant-name"))
    this.variantInput?.setAttribute("data-key", input.getAttribute("data-key"))

    if (!silent) fire(this.variantInput)

    this.dispatchEvent(
      new CustomEvent("fabric:pricechange", {
        bubbles: true,
        detail: {
          productId: this.dataset.fabricColorProductHandle,
          variantId: this.variantInput.value,
          price: this.#parsePriceValue(input.dataset.accessoryVariantPrice),
          compareAtPrice: this.#parsePriceValue(input.dataset.accessoryVariantCompareAtPrice),
          name: input.dataset.accessoryVariantName,
          key: input.dataset.key,
        },
      }),
    )

    this.updateMaterialDisplayFromInput(input)
  }

  getMaterialFromVariantName(name) {
    if (!name) return ""
    const [material] = name.split(",").map((s) => s.trim())
    return material || ""
  }

  getVariantTitleText(label, input) {
    if (!label && !input) return ""
    const titleEl = label?.querySelector(".variant-title")
    if (titleEl?.textContent) return titleEl.textContent.trim()
    const labelText = label?.querySelector(".variant-label")?.textContent?.trim()
    if (labelText) return labelText
    return input?.getAttribute("data-accessory-variant-name") || ""
  }

  updateTriggerText() {
    if (!this.cascadeTriggerText) return
    const checked = this.cascadeColors?.querySelector('input[type="radio"]:checked')
    if (!checked) {
      this.cascadeTriggerText.textContent = this.triggerPlaceholder
      return
    }
    const name = checked.getAttribute("data-accessory-variant-name") || ""
    this.cascadeTriggerText.textContent = name.replace(/\s*,\s*/, " – ")
  }

  isPanelOpen() {
    return this.cascadePanel && !this.cascadePanel.hidden
  }

  openPanel() {
    if (!this.cascadePanel) return
    if (this.isPanelOpen()) return
    // Re-set active group when re-opening so user always sees the group of the current selection
    const desired = this.getInitialActiveGroup()
    if (desired) this.setActiveGroup(desired)
    // On mobile: jump straight into the colors subpane if a variant is already picked,
    // otherwise start in the groups overview.
    const hasSelection = !!this.variantInput?.value
    this.setDetailView(this.isMobile() && hasSelection)

    // Portal panel to <body> so it escapes any local stacking context.
    if (!this.originalPanelParent) {
      this.originalPanelParent = this.cascadePanel.parentElement
      this.originalPanelNextSibling = this.cascadePanel.nextElementSibling
    }
    document.body.appendChild(this.cascadePanel)
    this.cascadePanel.setAttribute("data-portaled", "")

    this.cascadePanel.hidden = false
    this.positionPanel()

    this.cascadeTrigger?.setAttribute("aria-expanded", "true")
    this.cascadeRoot?.classList.add("fabric-cascade--open")
    document.addEventListener("click", this.boundOutsideClickHandler)
    document.addEventListener("keydown", this.boundEscapeHandler)
    window.addEventListener("scroll", this.boundReposition, true)
    window.addEventListener("resize", this.boundReposition)
  }

  closePanel() {
    if (!this.cascadePanel) return
    this.cascadePanel.hidden = true

    // Strip portal-mode inline styles
    this.cascadePanel.removeAttribute("data-portaled")
    this.cascadePanel.style.position = ""
    this.cascadePanel.style.top = ""
    this.cascadePanel.style.left = ""
    this.cascadePanel.style.width = ""
    this.cascadePanel.style.right = ""
    this.cascadePanel.style.zIndex = ""

    // Move panel back to its original parent so the picker DOM stays consistent.
    if (this.originalPanelParent) {
      if (this.originalPanelNextSibling && this.originalPanelNextSibling.parentElement === this.originalPanelParent) {
        this.originalPanelParent.insertBefore(this.cascadePanel, this.originalPanelNextSibling)
      } else {
        this.originalPanelParent.appendChild(this.cascadePanel)
      }
    }

    this.cascadeTrigger?.setAttribute("aria-expanded", "false")
    this.cascadeRoot?.classList.remove("fabric-cascade--open")
    document.removeEventListener("click", this.boundOutsideClickHandler)
    document.removeEventListener("keydown", this.boundEscapeHandler)
    window.removeEventListener("scroll", this.boundReposition, true)
    window.removeEventListener("resize", this.boundReposition)
  }

  positionPanel() {
    if (!this.cascadeTrigger || !this.cascadePanel) return
    if (!this.cascadePanel.hasAttribute("data-portaled")) return
    const rect = this.cascadeTrigger.getBoundingClientRect()
    this.cascadePanel.style.position = "fixed"
    this.cascadePanel.style.top = `${rect.bottom + 4}px`
    this.cascadePanel.style.left = `${rect.left}px`
    this.cascadePanel.style.width = `${rect.width}px`
    this.cascadePanel.style.right = "auto"
    this.cascadePanel.style.zIndex = "9999"
  }

  handleOutsideClick(e) {
    if (!this.isPanelOpen()) return
    if (this.contains(e.target)) return
    if (this.cascadePanel?.contains(e.target)) return
    this.closePanel()
  }

  handleEscape(e) {
    if (e.key === "Escape" && this.isPanelOpen()) {
      this.closePanel()
      this.cascadeTrigger?.focus()
    }
  }

  updateMaterialDisplayFromInput(input) {
    if (!input) return
    if (!this.materialDisplay || !document.body.contains(this.materialDisplay)) {
      this.materialDisplay = this.getMaterialDisplayTarget()
      this.materialDisplayThumb = this.materialDisplay?.querySelector(".material-selection-display__thumb")
      this.materialDisplayText = this.materialDisplay?.querySelector(".material-selection-display__text")
    }

    if (!this.materialDisplay) return
    const label = input.closest("label.variant-thumb")
    if (!label) {
      this.hideMaterialDisplay()
      return
    }

    const img = label.querySelector("img")
    const text = this.getVariantTitleText(label, input)

    if (this.materialDisplayThumb && img?.src) {
      this.materialDisplayThumb.style.backgroundImage = `url(${img.src})`
    }
    if (this.materialDisplayText) {
      this.materialDisplayText.textContent = text || ""
    }
    this.materialDisplay.hidden = false
  }

  hideMaterialDisplay() {
    if (!this.materialDisplay || !document.body.contains(this.materialDisplay)) {
      this.materialDisplay = this.getMaterialDisplayTarget()
      this.materialDisplayThumb = this.materialDisplay?.querySelector(".material-selection-display__thumb")
      this.materialDisplayText = this.materialDisplay?.querySelector(".material-selection-display__text")
    }
    if (!this.materialDisplay) return
    this.materialDisplay.hidden = true
    if (this.materialDisplayThumb) this.materialDisplayThumb.style.backgroundImage = ""
    if (this.materialDisplayText) this.materialDisplayText.textContent = ""
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.boundOutsideClickHandler)
    document.removeEventListener("keydown", this.boundEscapeHandler)
    window.removeEventListener("scroll", this.boundReposition, true)
    window.removeEventListener("resize", this.boundReposition)
    // If we get torn down while portaled, remove the orphaned panel from <body>.
    if (this.cascadePanel?.hasAttribute("data-portaled") && this.cascadePanel.parentElement === document.body) {
      this.cascadePanel.remove()
    }
  }

  getMaterialDisplayTarget() {
    const localTarget = this.querySelector("[data-material-display]")
    if (localTarget) return localTarget

    const key = this.dataset.materialDisplayKey || this.dataset.fabricColorProductHandle || this.colorFieldset?.dataset.productId

    if (key) {
      const safeKey = window.CSS && CSS.escape ? CSS.escape(key) : key
      const keyedTarget = document.querySelector(`[data-material-display-key="${safeKey}"]`)
      if (keyedTarget) return keyedTarget
    }

    const selector = this.dataset.materialDisplayTarget
    if (selector) {
      try {
        const externalTarget = document.querySelector(selector)
        if (externalTarget) return externalTarget
      } catch (e) {
        console.warn("Invalid selector for data-material-display-target:", selector, e)
      }
    }

    return null
  }

  #parsePriceValue(priceStr) {
    if (!priceStr || priceStr === "null" || priceStr === "undefined") return 0

    let cleaned = priceStr.replace(/[€$£¥\s]/g, "").trim()

    if (cleaned.includes(",")) {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    }

    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : Math.round(parsed * 100)
  }
}

customElements.define("variant-fabric-picker", VariantFabricPicker)
